/**
 * A layout check on demand: the document rendered as it will be read and
 * its pages probed — blank pages, a heading left at a page's foot, split
 * rows, clipped text — for free; and, only if asked, a model's look at the
 * pages, metered on the case. Each finding names its page; one placed on a
 * paragraph shows it.
 */

import { useState } from 'react';

import { Anchor, Button, Card, Checkbox, Group, Stack, Text } from '@mantine/core';

import * as Sentry from '@sentry/react';

import { requestAnchorHighlight } from '@/lib/anchorHighlight';
import { vnextApiClient } from '@/services/vnext-api';
import type { LayoutCheck } from '@/types/workbench';
import { toError } from '@/utils/errors';
import { formatUsd, words } from './format';

interface LayoutCheckPanelProps {
  reportId: string;
  kind: 'version' | 'draft';
  documentId: string;
}

export function LayoutCheckPanel({ reportId, kind, documentId }: Readonly<LayoutCheckPanelProps>) {
  const [look, setLook] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<LayoutCheck | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = async () => {
    setChecking(true);
    setError(null);
    try {
      setResult(await vnextApiClient.checkLayout(reportId, kind, documentId, look));
    } catch (err) {
      Sentry.captureException(toError(err));
      setError(toError(err, 'The check failed').message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Card withBorder padding="sm" radius="md" data-testid="layout-check">
      <Stack gap="xs">
        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
          Layout
        </Text>
        <Group gap="xs">
          <Button
            size="xs"
            variant="default"
            loading={checking}
            onClick={() => void check()}
            data-testid="layout-check-run"
          >
            Check the layout
          </Button>
          <Checkbox
            size="xs"
            label="A model looks too (a few cents)"
            checked={look}
            onChange={(e) => setLook(e.currentTarget.checked)}
            data-testid="layout-check-look"
          />
        </Group>
        {error ? (
          <Text size="xs" c="red">
            {error}
          </Text>
        ) : null}
        {result ? (
          <Stack gap={4} data-testid="layout-check-result">
            <Text size="xs" c="dimmed">
              {result.pageCount} pages
              {result.lookedAt.length > 0
                ? ` · a model looked at ${result.lookedAt.length} (${formatUsd(result.costUsd)})`
                : ''}
              {result.issues.length === 0 ? ' · nothing found' : ''}
            </Text>
            {result.issues.map((issue) => (
              <Text key={`${issue.page}-${issue.kind}-${issue.message}`} size="xs">
                Page {issue.page} · {words(issue.kind)}
                {issue.source === 'look' ? ' (the look)' : ''}: {issue.message}{' '}
                {issue.paraId ? (
                  <Anchor
                    component="button"
                    type="button"
                    size="xs"
                    onClick={() => issue.paraId && requestAnchorHighlight([issue.paraId])}
                  >
                    show
                  </Anchor>
                ) : null}
              </Text>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
}
